import { Divider, Dropdown } from "antd"
import dayjs from "dayjs"
import {
  ArrowDown,
  ArrowUp,
  Check,
  ChevronRight,
  Copy,
  FileText,
  InfoIcon,
  RotateCcw,
  Telescope
} from "lucide-react"
import { useState } from "react"
import { useTranslation as useRetryTranslation } from "react-i18next"

/**
 * Inline "Retry" button next to a failed assistant bubble. Calls the
 * /retry endpoint via sendMessageRef, which re-runs the existing plan
 * — the BE skips already-completed steps (Gate -1) and only re-fires
 * failed/blocked ones. Used after we deploy a fix and the user wants
 * to recover the turn without re-typing the prompt.
 *
 * Hidden when we don't have a failed_invocation_id to reference (e.g.
 * pre-v2 turns from history that lack the field) — there's nothing
 * useful to point the BE at in that case.
 */
function RetryButton({
  sessionId,
  assistantMessageId,
  failedInvocationId,
  sendMessageRef
}: {
  sessionId: string
  assistantMessageId: string
  failedInvocationId?: string
  sendMessageRef: any
}) {
  const { t } = useRetryTranslation()
  const [submitting, setSubmitting] = useState(false)
  if (!sessionId || !assistantMessageId || !failedInvocationId) return null
  const onClick = () => {
    if (submitting) return
    setSubmitting(true)
    try {
      // Smart-retry: if the bubble has a `pendingResumeDecision` stash
      // (a /resume call that 409'd before the user's ask_user answer
      // could be applied), re-fire the SAME resume payload. The BE's
      // stale-stub recovery accepts the second attempt now that the
      // mid-stream `awaiting_user` persist has had time to land.
      //
      // Falling back to retry_execution when there's no pending
      // decision keeps the failed-plan recovery path intact.
      const cur =
        useOsAgentsIdStore.getState().messages?.[sessionId]?.[
          assistantMessageId
        ] || {}
      const pending = (cur as any).pendingResumeDecision
      const resume = sendMessageRef?.current?.resume
      const retry = sendMessageRef?.current?.retry
      if (pending && typeof resume === "function") {
        resume(sessionId, assistantMessageId, pending)
      } else if (typeof retry === "function") {
        retry(sessionId, assistantMessageId, failedInvocationId)
      }
    } finally {
      // The retry call kicks off an async stream. We flip back after
      // the next tick so the button is clickable again if the user
      // hits Retry repeatedly (each click aborts the prior stream
      // server-side via the /retry in-flight guard, which 409s).
      setTimeout(() => setSubmitting(false), 800)
    }
  }
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={submitting}
      className="inline-flex items-center gap-1 text-xs px-2 py-[5px] rounded-lg border border-light-border dark:border-border bg-white dark:bg-dark-2 hover:bg-genesis dark:hover:bg-dark-3 disabled:opacity-50"
      data-testid="agent-msg-retry"
    >
      <RotateCcw size={12} />
      <span>{t("os_agents.cta_retry")}</span>
    </button>
  )
}

function mapOutline(raw: any[]) {
  return raw.map((c: any, i: number) => ({
    id: c.id || `card-${i}-${Date.now()}`,
    title: c.title || `Slide ${i + 1}`,
    type: c.type || "content",
    layout: c.layout,
    bullets: Array.isArray(c.bullets) ? c.bullets.filter(Boolean) : [],
    userNotes: c.user_notes || c.userNotes || "",
    data_source: c.data_source,
    search_keywords: c.search_keywords,
    account_ids: c.account_ids
      ? c.account_ids
      : c.account_id
        ? [c.account_id]
        : undefined
  }))
}
import { useCallback, useEffect, useMemo, useRef } from "react"
import { useTranslation } from "react-i18next"
import { useParams } from "react-router"

import AgentQuoteSelection from "~/components/agent-quote-selection"
import RichTextEditor from "~/components/base-editor/RichTextEditor"
import { EVENTS, getCardPreviewKey } from "~/containers/os-agents/constants"
import { useGetAgentTools, useGetKosTools } from "~/hooks/os.agents"
import { useCopyToClipboardFormatted } from "~/hooks/useClipboard"
import useGlobalStore from "~/store/global"
import useOsAgentsIdStore from "~/store/os.agents_.$id"
import useRfpOsQnaStore from "~/store/rfp-os.qna"
import PptSessionLinkCard from "~/components/ppt-session-link-card"
import {
  getSuggestionLabel,
  getSuggestionPrompt,
  type SuggestionItem
} from "~/types/knowledge-os"
import { track } from "~/utils/amplitude"
import { getBaseEventProps } from "~/utils/amplitude-events"
import { getFileTypeAndIcon, removeDuplicates } from "~/utils/helpers"
import {
  parseQuotedMessage,
  stripInternalAccountId
} from "~/utils/parse-quoted-message"

import AgentSelectAccount from "./agent-select-account"
import AgentSessionHistory from "./agent-session-history"
import AgentStepsUI from "./components/agent-steps-ui-v2"
import ThoughtTimer from "./components/thought-timer"

export default function AgentIdMessages({
  sendMessageRef,
  sessionIdOverride,
  mode = "ask",
  embedded = false
}: {
  sendMessageRef: any
  /** RFP-OS passes its own sub-session id; otherwise we read from
   *  the URL (Ask-OS route param). Empty string ⇒ no messages yet. */
  sessionIdOverride?: string
  /** Mirrors AgentSearchV2's mode. When "rfp", we skip mounting
   *  AgentSessionHistory — that component is Ask-OS chrome (chat-
   *  list sidebar + empty-history poller) and RFP-OS has its own
   *  SubSessionHistory rendered by BuildWithAgent. Without this
   *  guard, the poller fired `/v1/.../session/{parent_rfp_id}/
   *  history` on a loop because useParams().id is the rfp_id, not
   *  a chat session. */
  mode?: "ask" | "rfp"
  /** When true (workbench/overlay context — sales-pitch or PMB v2),
   *  skips the AgentSessionHistory shell. That component reads
   *  useParams().id, which outside /os/agents/:id is empty or an
   *  unrelated route id, triggering the empty-history poller + a
   *  spurious top-of-pane spinner. */
  embedded?: boolean
}) {
  const { t } = useTranslation()
  const messagesEndRef = useRef<HTMLDivElement>(null)
  const scrollContainerRef = useRef<HTMLDivElement>(null)
  const lastScrollTop = useRef(0)
  const isAtBottomRef = useRef(true)
  // ENG-1907: show a floating "scroll to latest" button when the user has
  // scrolled up (e.g. reading while streaming continues below).
  const [showScrollDown, setShowScrollDown] = useState(false)
  const { data: agentToolsData } = useGetAgentTools()
  const { data: kosToolsData } = useGetKosTools()
  const agentToolsList = agentToolsData?.data ?? []
  const kosToolsList = kosToolsData?.data ?? []
  const toolsList = [...agentToolsList, ...kosToolsList]
  const { id: routeId = "" } = useParams()
  const existingSessionId =
    typeof sessionIdOverride === "string" ? sessionIdOverride : routeId
  const messages = useOsAgentsIdStore(
    (store) => store.messages?.[existingSessionId]
  )
  // Only auto-scroll when the user is already near the bottom. Without
  // this guard every store mutation (incl. the ~periodic `heartbeat` /
  // `progress` SSE patches while a plan runs) yanked the view down,
  // fighting users who scrolled up to read — ENG-1336.
  const handleScroll = useCallback(() => {
    const el = scrollContainerRef.current
    if (!el) return
    const distanceFromBottom = el.scrollHeight - el.scrollTop - el.clientHeight
    const isScrollingUp = el.scrollTop < lastScrollTop.current
    lastScrollTop.current = el.scrollTop
    if (isScrollingUp) isAtBottomRef.current = false
    else if (distanceFromBottom < 100) isAtBottomRef.current = true
    // Reveal the button once clearly above the bottom; hide near the tail so
    // it doesn't flicker.
    setShowScrollDown(distanceFromBottom > 200)
  }, [])

  const scrollToBottom = useCallback(() => {
    isAtBottomRef.current = true
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" })
    setShowScrollDown(false)
  }, [])

  useEffect(() => {
    if (messages && Object.keys(messages).length && isAtBottomRef.current) {
      messagesEndRef.current?.scrollIntoView()
    }
    // Content can grow while the user is scrolled up (streaming) with no scroll
    // event fired — recompute the button's visibility here too so it appears.
    const el = scrollContainerRef.current
    if (el) {
      setShowScrollDown(el.scrollHeight - el.scrollTop - el.clientHeight > 200)
    }
  }, [messages])

  const messagesList = messages && Object.values(messages)
  return (
    // `min-h-0` lets this flex child actually shrink past its
    // intrinsic content size, which is what makes `overflow-auto`
    // engage. Without it the chat list grows unbounded and the
    // surrounding column stops scrolling (RFP-OS V2 panel hit this).
    <div className="relative flex flex-col flex-1 min-h-0">
      <div
        className="overflow-auto flex-1 min-h-0"
        ref={scrollContainerRef}
        onScroll={handleScroll}
      >
        <div
          className={`flex-1 flex flex-col gap-4 pt-6 pb-4 max-w-4xl mx-auto ${existingSessionId ? "px-4" : "px-10"}`}
        >
          {mode === "ask" && !embedded && (
            <AgentSessionHistory
              sendMessageRef={sendMessageRef}
              hasNewMessage={messagesList?.length > 0}
            />
          )}
          {messagesList?.map((item: any, index: number) => (
            <MessageItem
              key={item.id}
              item={item}
              toolsList={toolsList}
              sendMessageRef={sendMessageRef}
              isLast={index === messagesList?.length - 1}
              sessionIdOverride={sessionIdOverride}
              mode={mode}
            />
          ))}
          <div ref={messagesEndRef} />
        </div>
      </div>
      {showScrollDown && (
        <button
          type="button"
          onClick={scrollToBottom}
          aria-label={t("os_agents.cta_scroll_to_latest", {
            defaultValue: "Scroll to latest"
          })}
          className="absolute bottom-4 right-4 z-10 flex h-9 w-9 items-center justify-center rounded-full border border-light-border bg-white text-dark-2 shadow-md transition-colors hover:bg-genesis dark:border-border dark:bg-dark-2 dark:text-dark-6 dark:hover:bg-dark-3"
        >
          <ArrowDown size={18} />
        </button>
      )}
    </div>
  )
}

const MessageItem = ({
  item,
  toolsList,
  sendMessageRef,
  isLast,
  sessionIdOverride,
  mode = "ask"
}: {
  item: any
  toolsList: any
  sendMessageRef: any
  isLast: boolean
  sessionIdOverride?: string
  mode?: "ask" | "rfp"
}) => {
  const { t } = useTranslation()
  const { id: routeId = "" } = useParams()
  const existingSessionId =
    typeof sessionIdOverride === "string" ? sessionIdOverride : routeId
  // Sources-drawer routing depends on the surface — Ask-OS uses its
  // own sidebar drawer (useOsAgentsIdStore), RFP-OS has a dedicated
  // SourcesDrawer rendered inside BuildWithAgent (useRfpOsQnaStore).
  // Pick the right setter at render time so the click on the Sources
  // button opens the drawer that's actually mounted.
  const setAskOsSourcesDrawerData = useOsAgentsIdStore(
    (store) => store.setSourcesDrawerData
  )
  const setRfpOsSourcesDrawerData = useRfpOsQnaStore(
    (store) => store.setSourcesDrawerData
  )
  const setSourcesDrawerData =
    mode === "rfp" ? setRfpOsSourcesDrawerData : setAskOsSourcesDrawerData
  // Same store-backed quote affordance Ask-OS V1 uses
  // (agent-id-messages.tsx#L111). AgentSearchV2's chat input reads
  // store.quotedText too, so a click on "quote" inside an assistant
  // message ends up as `context_quote` on the next send.
  const setQuotedText = useOsAgentsIdStore((store) => store.setQuotedText)
  const pendingMessage = useOsAgentsIdStore((store) => store.pendingMessage)
  const [copiedText, copy] = useCopyToClipboardFormatted()
  const currentUserEmail = useGlobalStore((store) => store.user?.email)
  const isUser = item.role === "user"
  const displayEmail = isUser ? item.userEmail || currentUserEmail : null
  const contentType = item.content_type
  const isToolMessage = contentType === "tool"
  const isUiComponentMessage =
    contentType === "ui_component" && item.content.resourceName
  const isAccountSelectionTool = contentType === "account_selection"
  const isPending = item.isPending
  const errorMessage = item.error

  // Sources can arrive in two shapes:
  //  - Flat array (Ask-OS old shape): just internal sources, no web.
  //  - Object (RFP-OS shape, V1 endpoint): `{mlRecommendedSearchDocs:
  //    [...], web_search_results: [...] | {sources: [...]}}`.
  // Mirrors V1 message-sources.tsx parsing so the same data the V1
  // RFP UI uses renders identically here.
  const _rawSources = item.llmVerifiedSources || item.source
  let _internalSources: any[] = []
  let _webResults: any[] = []
  if (Array.isArray(_rawSources)) {
    _internalSources = _rawSources
  } else if (_rawSources && typeof _rawSources === "object") {
    _internalSources = Array.isArray(_rawSources.mlRecommendedSearchDocs)
      ? _rawSources.mlRecommendedSearchDocs
      : []
    const ws = _rawSources.web_search_results
    if (Array.isArray(ws)) {
      _webResults = ws
    } else if (ws && Array.isArray(ws.sources)) {
      _webResults = ws.sources
    }
  }
  const uniqueSources = removeDuplicates(_internalSources, "name")
  const webResults = _webResults

  let content = ""
  if (isUser) {
    content = typeof item.content === "string" ? item.content : item.content[0]
  } else {
    if (isToolMessage) {
      content = item.content.resourceParams?.text || ""
    } else {
      content = item.content?.parts ? item.content.parts[0] : ""
    }
  }

  // For user messages, strip the embedded quote preamble so the bubble
  // shows just the user's question; the excerpt renders separately
  // above. Same parser Ask-OS V1 (agent-id-messages.tsx#L141) uses.
  const parsedUserMessage = isUser ? parseQuotedMessage(content) : null
  const userQuoteExcerpt = parsedUserMessage?.contextQuote ?? null
  const displayContent = isUser
    ? stripInternalAccountId(
        userQuoteExcerpt ? parsedUserMessage!.userMessage : content
      )
    : content

  // Detect PPT session links in assistant messages
  const pptLinkMatch =
    !isUser && content
      ? content.match(
          /\[Open Presentation:\s*(.+?)\]\((\/k-os\/presentation-builder\?session=[^)]+)\)/
        )
      : null
  const pptTitle = pptLinkMatch?.[1] || ""
  const pptUrl = pptLinkMatch?.[2] || ""

  // Standalone error path — only fires when the failed message has
  // NO step rows / no ask_user / no user-input artifact to surface.
  // When the message has step rows (e.g. "You chose: answer_question
  // → Sitecore, Acquia, …"), fall through to the main render so the
  // user can SEE the input they gave before the failure. Without this
  // they'd see a bare error toast and assume their answer was lost.
  // Retry button only renders on the LAST message — there's no
  // benefit to a retry mid-history (the latest failure is what we're
  // recovering from). Avoids the "5 retry buttons stacked" UX.
  const hasArtifacts =
    !!item.agentSteps?.length || !!item.askUser || !!item.content
  if (errorMessage && !isUser && !hasArtifacts) {
    return (
      <div className="flex items-center gap-2">
        <div className="border border-primary dark:bg-dark-3 p-1 rounded-full flex-shrink-0">
          <img src="/favicon.png" alt="" className="w-5 h-5" />
        </div>
        <div className="border border-red-400 dark:border-red-300 rounded-xl px-3 py-[6px] flex items-center gap-2 w-fit text-xs text-red-400 dark:text-red-300">
          <InfoIcon size={16} />
          {errorMessage ||
            "We are having trouble serving your request at the moment. Please contact support@orbitshift.ai if the issue persists"}
        </div>
        {isLast && (
          <RetryButton
            sessionId={existingSessionId}
            assistantMessageId={item.id}
            failedInvocationId={item.invocationId}
            sendMessageRef={sendMessageRef}
          />
        )}
      </div>
    )
  }

  return (
    <>
      <div
        className={`flex flex-col gap-2 relative ${
          isUser ? "w-fit max-w-[70%] ml-auto" : "max-w-[80%]"
        }`}
      >
        {item.files?.length > 0 && (
          <div className="flex flex-wrap items-center gap-2">
            {item.files.map((file: File) => {
              const fileType = getFileTypeAndIcon(file.name)
              return (
                <div
                  key={file.name}
                  className="flex items-start gap-2 bg-white dark:bg-dark-2 border border-light-border dark:border-border px-2 py-[6px] rounded-lg"
                >
                  <div className={`p-2 rounded-md ${fileType.color}`}>
                    <FileText size={16} className="text-dark-6" />
                  </div>
                  <div className="flex flex-col">
                    <span className="text-xs text-dark-2 dark:text-dark-6 text-ellipsis max-w-24 overflow-hidden whitespace-nowrap">
                      {file.name}
                    </span>
                    <span className="inline-flex items-center gap-1.5 text-xs text-dark-2 dark:text-dark-6">
                      {(file as { is_template?: boolean }).is_template && (
                        <span className="px-[6px] py-px rounded-full bg-primary/10 text-primary dark:text-primary-light text-[10px] font-bold tracking-wide uppercase">
                          {t("ask_os.label_template_tag")}
                        </span>
                      )}
                      {fileType.type}
                    </span>
                  </div>
                </div>
              )
            })}
          </div>
        )}
        {/* ThoughtTimer is the pre-existing canned/timed animation. Keep
            it ONLY as a placeholder while the v2 pipeline hasn't emitted
            anything yet (first ~1-3s of a fresh query). As soon as there
            are real agent steps to render, OR the pipeline paused on
            ask_user, OR an error was surfaced — suppress the canned
            animation and let AgentStepsUI / the error bubble take over.
            Without this guard, 500 errors / aborted streams leak "Step 2
            of 2 — 1 min left" indefinitely even after isPending flips
            off on some edge paths. */}
        {!isUser &&
          !(item.agentSteps?.length || item.askUser || item.error) && (
            <ThoughtTimer isPending={isPending} messageId={item.id} />
          )}
        {/* User-bubble quote preview — same shape as Ask-OS V1
            (agent-id-messages.tsx#L204-L211): renders the excerpt the
            user quoted, above their bubble. */}
        {isUser && userQuoteExcerpt && (
          <div className="flex items-start gap-1.5 text-xs text-dark-2/60 dark:text-dark-6/60 border-l-2 border-primary/40 pl-2 self-stretch">
            <span className="text-primary/60 flex-shrink-0">↳</span>
            <p className="line-clamp-2 italic m-0 text-left flex-1">
              {userQuoteExcerpt}
            </p>
          </div>
        )}
        {/* v2 pipeline: per-frame progress UI + inline ask-user prompt.
            Rendered ABOVE the final content so the user sees the
            plan/steps/critic as they happen. Persists after `done` so
            the user can scroll back to inspect reasoning. */}
        {!isUser && (item.agentSteps?.length || item.askUser) && (
          <div className="pl-11">
            <AgentStepsUI
              steps={item.agentSteps || []}
              askUser={item.askUser || null}
              isPending={isPending}
              // PMB v2 "Pre-meeting brief" tool card → auto-answer the
              // Q1/Q3 picker with mode="default" (set on the message by
              // sendMessage when the card was clicked).
              forceDefault={Boolean((item as any)._pmbForceDefault)}
              // ENG-1316: only auto-collapse when the worker's terminal
              // `done` has landed — i.e., content is set, no askUser,
              // no error. Every mid-flight pause (plan_approval,
              // clarification, error) leaves this false so the panel
              // stays open.
              isFinalAnswer={Boolean(content) && !errorMessage && !item.askUser}
              onResume={(decision) => {
                const resume = sendMessageRef?.current?.resume
                if (typeof resume !== "function") return
                resume(existingSessionId, item.id, decision)
              }}
            />
          </div>
        )}
        {displayContent &&
          (!isUser ? (
            <div data-testid="agent-final-answer">
              <AgentQuoteSelection
                onQuote={setQuotedText}
                disabled={isPending || item?.status === "IN_PROGRESS"}
              >
                <RichTextEditor
                  hideToolBar
                  data-testid="agent-final-answer-text"
                  className="p-0 p-13 text-dark-2 dark:text-dark-6 flex-1 justify-start !pl-11"
                  content={displayContent}
                  contentClassName="transparent-body"
                />
              </AgentQuoteSelection>
            </div>
          ) : (
            <div className="ml-auto w-fit flex flex-col items-end gap-1.5 px-4 py-[10px] bg-genesis dark:bg-dark-2 rounded-[10px]">
              <RichTextEditor
                hideToolBar
                className="p-0 p-13 text-dark-2 dark:text-dark-6 flex justify-end"
                content={displayContent}
                contentClassName="transparent-body"
              />
              {item.researchEnabled && (
                <p
                  data-testid="agent-msg-deep-research-tag"
                  className="text-xs flex items-center gap-1.5 bg-sky-400 text-white px-2 py-1 rounded-lg w-fit"
                >
                  <Telescope size={14} /> {t("rfp_os.label_deep_research")}
                </p>
              )}
            </div>
          ))}
        {isUser && (displayEmail || item.timestamp) && (
          <p
            data-testid="agent-msg-question-meta"
            className="text-xs text-right text-gray-500 dark:text-gray-400 ml-auto w-fit"
          >
            {displayEmail && <span>{displayEmail}</span>}
            {displayEmail && item.timestamp && <span> • </span>}
            {item.timestamp && (
              <span>
                {dayjs(item.timestamp).format("MMM D, YYYY · h:mm A")}
              </span>
            )}
          </p>
        )}
        {errorMessage && !isUser && (
          <div className="flex items-center gap-2">
            <div className="border border-red-400 dark:border-red-300 rounded-xl px-3 py-[6px] flex items-center gap-2 w-fit text-xs text-red-400 dark:text-red-300">
              <InfoIcon size={16} />
              {errorMessage ||
                "We are having trouble serving your request at the moment. Please contact support@orbitshift.ai if the issue persists"}
            </div>
            {/* Retry only on the latest message — see the standalone-
                error early-return for the same gating. */}
            {isLast && (
              <RetryButton
                sessionId={existingSessionId}
                assistantMessageId={item.id}
                failedInvocationId={item.invocationId}
                sendMessageRef={sendMessageRef}
              />
            )}
          </div>
        )}
        {isAccountSelectionTool && (
          <div className="max-w-2xl pl-11">
            <AgentSelectAccount
              label={t("os_agents.placeholder_select_an_account")}
              sendMessageRef={sendMessageRef}
            />
          </div>
        )}
        {isUiComponentMessage && (
          <>
            {Array.isArray((item.content as any)?.cards) &&
            (item.content as any).cards.length > 0 ? (
              <div className="flex flex-col gap-2">
                {(item.content as any).cards.map((card: any, idx: number) => (
                  <div
                    key={card.stepId || card.resourceName || idx}
                    data-testid="agent-ui-component-card-item"
                  >
                    <ToolName
                      toolsList={toolsList}
                      resourceName={card.resourceName}
                      content={{
                        // Render only this card via the splitter — pass
                        // the per-card content so the drawer shows the
                        // right params when ToolName's onClick fires.
                        ...(item.content as any),
                        resourceName: card.resourceName,
                        resourceParams: card.resourceParams,
                        // ENG-1905: stable per-card identity so clicking one
                        // card while another preview is open SWITCHES to it
                        // instead of closing, and the open card is highlighted.
                        // Shared with agent-search-v2's auto-open so both
                        // resolve to the same key (see getCardPreviewKey).
                        _previewKey: getCardPreviewKey(item.id, card, idx)
                      }}
                      sendMessageRef={sendMessageRef}
                    />
                  </div>
                ))}
              </div>
            ) : (
              <div data-testid="agent-ui-component-card-single">
                <ToolName
                  toolsList={toolsList}
                  resourceName={item.content.resourceName}
                  content={{ ...item.content, _previewKey: item.id }}
                  sendMessageRef={sendMessageRef}
                />
              </div>
            )}
          </>
        )}

        {pptUrl && (
          <div className="pl-11 pt-2">
            <PptSessionLinkCard title={pptTitle} sessionUrl={pptUrl} />
          </div>
        )}
        {(uniqueSources.length > 0 || webResults.length > 0) && (
          <div className="flex flex-row gap-2 border-t border-light-border dark:border-border pt-4 ml-11 items-center">
            {uniqueSources.length > 0 && (
              <button
                data-testid="agent-sources-open"
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-white dark:bg-dark-2 border border-light-border dark:border-border hover:bg-gray-50 dark:hover:bg-dark-1 transition-colors"
                onClick={() => {
                  track("source_clicked", {
                    source_name: "View all sources",
                    sources_available: uniqueSources.map(
                      (source: any) => source.name
                    ),
                    triggered_from:
                      mode === "rfp" ? "rfpos_qna_v2" : "os_agents"
                  })
                  setSourcesDrawerData(uniqueSources)
                }}
              >
                <span className="text-xs font-medium text-dark-2 dark:text-dark-6">
                  {t("label_sources")}
                </span>
                <ArrowDown className="w-3 h-3 text-dark-2 dark:text-dark-6 rotate-[-90deg]" />
              </button>
            )}
            {webResults.length > 0 && (
              <Dropdown
                placement="bottomLeft"
                // Cap the popover height so a long external-sources list
                // scrolls inside the dropdown instead of overflowing the
                // viewport (proactive decks can return 15+ web sources).
                overlayStyle={{ maxHeight: 360, overflowY: "auto" }}
                menu={{
                  items: webResults.map((result: any, idx: number) => ({
                    key: result.id || result.link || idx,
                    label: (
                      <a
                        className="flex gap-2 text-xs text-primary dark:text-primary-light underline"
                        href={result.link}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        {result.title || result.link}
                      </a>
                    )
                  }))
                }}
              >
                <button className="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-white dark:bg-dark-2 border border-light-border dark:border-border hover:bg-gray-50 dark:hover:bg-dark-1 transition-colors text-xs font-medium text-dark-2 dark:text-dark-6">
                  {t("rfp_os.label_external_sources")}
                </button>
              </Dropdown>
            )}
          </div>
        )}
        {item.score?.score_category && item.score?.score_category !== "NA" && (
          <p className="rounded-lg text-xs bg-genesis dark:bg-dark-1 text-primary dark:text-blue-300 border-none mt-1">
            Confidence:{" "}
            <span className="font-semibold">
              {item.score.score_category.replace(/Confidence/gi, "")}
            </span>
          </p>
        )}
        {!isUser && !isPending && content && (
          <div className="mt-4 border-t border-light-border dark:border-border flex items-center pt-2 ml-11">
            <button
              type="button"
              data-testid="agent-copy-button"
              onClick={() => copy(content)}
              aria-label={copiedText ? "Copied" : "Copy"}
              className={`
                flex items-center gap-2 px-2 py-1 cursor-pointer
                font-small text-xs
                text-dark-2 opacity-70 dark:text-dark-6 hover:opacity-90
                transition-colors mr-auto
                ${copiedText ? "text-grey-100 dark:text-dark-6" : ""}
              `}
            >
              {copiedText ? (
                <>
                  <Check className="w-4 h-4 text-dark-2 dark:text-dark-6" />
                  {t("label_copied")}
                </>
              ) : (
                <>
                  <Copy className="w-4 h-4 text-dark-2 dark:text-dark-6" />{" "}
                  {t("label_copy")}
                </>
              )}
            </button>
          </div>
        )}
      </div>
      {item.suggestions && item.suggestions.length > 0 && (
        <>
          <div className="flex flex-col gap-2 items-end">
            <h3
              data-testid="agent-suggested-actions-heading"
              className="text-xs tracking-wide text-gray-600 dark:text-gray-400"
            >
              {t("label_suggested_actions")}
            </h3>
            {item.suggestions.map(
              (suggestion: SuggestionItem, index: number) => {
                const label = getSuggestionLabel(suggestion)
                const prompt = getSuggestionPrompt(suggestion)
                return (
                  <button
                    key={index}
                    data-testid="agent-suggested-action"
                    className="w-fit text-left flex items-center gap-2 text-xs text-dark-2 dark:text-dark-6 border border-secondary bg-white dark:bg-dark-3 px-3 py-1 rounded-xl"
                    onClick={() => {
                      track(EVENTS.SUGGESTION_CLICKED, {
                        ...getBaseEventProps({ feature: "os_agents" }),
                        suggestion_label: label
                      })
                      sendMessageRef.current?.({
                        tool_id: "",
                        agentQuery: prompt,
                        displayText: label
                      })
                    }}
                    disabled={!!pendingMessage}
                  >
                    {label}
                    <div
                      className={`bg-primary dark:text-primary-light flex-shrink-0 w-6 h-6 flex items-center justify-center rounded-lg`}
                    >
                      <ArrowUp size={12} className="text-dark-6" />
                    </div>
                  </button>
                )
              }
            )}
          </div>
        </>
      )}
      {!isUser &&
        item.suggestions &&
        item.suggestions.length > 0 &&
        !isLast && <Divider className="m-0" />}
    </>
  )
}

/** ENG-2503 — "PPT Generated V<n>", n = card position in this chat session's
 *  v2 deck cards. Scoped per session, not per param: generate cards carry
 *  opportunity_id, iterate cards ppt_session_id, so either key splits the
 *  series. Null for non-v2 cards. */
const usePitchDeckV2CardLabel = (
  resourceName: string,
  content: any
): string | null => {
  const allMessages = useOsAgentsIdStore((store) => store.messages)
  const slideImages = useOsAgentsIdStore((store) => store.pitchDeckSlideImages)
  const rp = content?.resourceParams
  const previewKey = content?._previewKey
  const opportunityName = rp?.opportunity_name

  return useMemo(() => {
    if (resourceName !== "SalesPitchDeckBuilderV2" || !previewKey) return null

    // Per-session so another opportunity's deck cards aren't counted.
    const collect = (session: any): string[] => {
      const keys: string[] = []
      Object.values(session || {}).forEach((msg: any) => {
        const msgContent = msg?.content
        if (!msgContent) return
        const cards = Array.isArray(msgContent.cards) ? msgContent.cards : []
        if (cards.length > 0) {
          cards.forEach((card: any, idx: number) => {
            if (card?.resourceName === "SalesPitchDeckBuilderV2") {
              keys.push(getCardPreviewKey(msg.id, card, idx))
            }
          })
          return
        }
        if (msgContent.resourceName === "SalesPitchDeckBuilderV2") {
          keys.push(msg.id)
        }
      })
      return keys
    }

    let keys: string[] = []
    let index = -1
    for (const session of Object.values(allMessages || {})) {
      const sessionKeys = collect(session)
      const at = sessionKeys.indexOf(previewKey)
      if (at >= 0) {
        keys = sessionKeys
        index = at
        break
      }
    }

    // Unknown card (not committed to the store yet): treat it as the newest.
    const version = index >= 0 ? index + 1 : keys.length + 1
    const isLatest = index < 0 || index === keys.length - 1
    const isReady = !isLatest || (slideImages?.length ?? 0) > 0
    const suffix = opportunityName ? ` · ${opportunityName}` : ""
    return isReady
      ? `PPT Generated V${version}${suffix}`
      : `Generating PPT…${suffix}`
  }, [resourceName, previewKey, opportunityName, allMessages, slideImages])
}

export const ToolName = ({
  toolsList,
  resourceName,
  content
}: {
  toolsList: any[]
  resourceName: string
  content: any
  sendMessageRef: any
}) => {
  const { t } = useTranslation()
  const tool = toolsList?.find((tool) => tool.id === resourceName)
  const showSplitter = useOsAgentsIdStore((store) => store.showSplitter)
  const setShowSplitter = useOsAgentsIdStore((store) => store.setShowSplitter)
  const pptOutlineState = useOsAgentsIdStore((store) => store.pptOutlineState)
  const isPptAgent =
    resourceName === "GeneratePresentationBuilderAgent" ||
    resourceName === "PresentationOutlineBuilder"
  const pptSessionId = isPptAgent ? content?.resourceParams?.session_id : null
  const pptCards = pptOutlineState.cards.length
  const pptSlides = (pptOutlineState.slides as any[])?.length || 0
  const pptTarget = pptOutlineState.requestedCount
  const pptLabel = isPptAgent
    ? !pptOutlineState.hasStarted
      ? pptSessionId
        ? "Loading..."
        : "Presentation"
      : pptOutlineState.slidesStatus === "ready"
        ? `Slides Ready (${pptSlides})`
        : pptOutlineState.slidesStatus === "generating"
          ? `Generating Slides... ${pptSlides}/${pptCards || pptTarget}`
          : pptCards > 0 && pptCards < pptTarget
            ? `Generating Outline... ${pptCards}/${pptTarget}`
            : pptOutlineState.isStreaming
              ? `Generating Outline... ${pptCards}/${pptTarget}`
              : pptCards > 0
                ? `Outline Ready (${pptCards})`
                : "Presentation"
    : null
  // Restore PPT state from backend on mount
  const tokenInfo = useGlobalStore((store) => store.tokenInfo)
  useEffect(() => {
    if (
      !isPptAgent ||
      pptOutlineState.hasStarted ||
      !pptSessionId ||
      !tokenInfo
    )
      return
    import("~/api/ppt-ai-generator").then(({ fetchPptSession }) => {
      fetchPptSession(tokenInfo, pptSessionId)
        .then((res: any) => {
          const session = res?.data?.data || res?.data
          if (!session) return
          const setPptState = useOsAgentsIdStore.getState().setPptOutlineState
          const requestedCount = session.messages?.[0]?.slide_count || 8
          if (session.slides && session.slides.length > 0) {
            const outlineCount = session.outline?.length || requestedCount
            const slidesComplete = session.slides.length >= outlineCount
            const isIterating = session.status === "iterating"
            setPptState({
              hasStarted: true,
              sessionId: pptSessionId,
              slidesStatus: isIterating
                ? "generating"
                : slidesComplete
                  ? "ready"
                  : "generating",
              cards: mapOutline(session.outline || []),
              slides: session.slides,
              requestedCount
            })
            if (isIterating) {
              const setShowSplitter =
                useOsAgentsIdStore.getState().setShowSplitter
              setShowSplitter({
                resourceName: "PresentationOutlineBuilder",
                resourceParams: {
                  session_id: pptSessionId,
                  status: "slides_updated"
                }
              })
            }
          } else if (session.outline && session.outline.length > 0) {
            const isIncomplete = session.outline.length < requestedCount
            setPptState({
              hasStarted: true,
              sessionId: pptSessionId,
              cards: mapOutline(session.outline),
              requestedCount,
              isStreaming: isIncomplete
            })
          }
        })
        .catch((_) => {
          void _
        })
    })
  }, [isPptAgent, pptOutlineState.hasStarted, pptSessionId, tokenInfo])

  const rp = content?.resourceParams
  // ENG-2503: backend `title` is written mid-run and never rewritten, so it
  // said "Processing your request" forever. Label v2 decks from FE state.
  const pdv2 = usePitchDeckV2CardLabel(resourceName, content)
  const baseLabel =
    pdv2 ||
    pptLabel ||
    // Must precede the bare "Sales Pitch Deck" fallback below — same
    // resourceName, so the fallback won first and this branch was dead code.
    (resourceName === "SalesPitchDeckBuilderV2"
      ? content?.resourceParams?.opportunity_name
        ? `Pitch Deck — ${content.resourceParams.opportunity_name}`
        : "Sales Pitch Deck"
      : null) ||
    (resourceName === "SalesPitchDeckBuilder" ? "Sales Pitch Deck" : null) ||
    (resourceName === "PptBuilderV2"
      ? content?.resourceParams?.topic_label
        ? `PowerPoint — ${content.resourceParams.topic_label}`
        : content?.resourceParams?.mode === "iterate"
          ? "Updating presentation…"
          : "PowerPoint"
      : null) ||
    (resourceName === "GeneratePersonPreMeetingBriefAgent" ||
    resourceName === "GeneratePreMeetingBriefAgentV2"
      ? "Pre-meeting brief"
      : null) ||
    tool?.name ||
    "Fetching name..."
  // ENG-1904: account-scoped cards (news, account plan, etc.) don't expose the
  // account in their FE params, so the backend sends a ready-to-display card
  // title (`title` in the ui_component params, e.g. "Get key news for ABN
  // Amro") to keep multiple cards distinguishable. Prefer it; else the tool
  // name.
  const backendTitle = typeof rp?.title === "string" ? rp.title.trim() : ""
  // pdv2 wins over the backend title on purpose — see ENG-2503 above.
  const label = pdv2 || backendTitle || baseLabel

  // Is THIS card the one currently open in the right-side preview panel? Its
  // stable `_previewKey` matches the open splitter's. Drives the click toggle
  // (ENG-1905: switch to a different card, or close the same one) AND the
  // active-card highlight so the user can see which card is showing —
  // deep-comparing the whole `content` folded in the shared `cards` array and
  // could make distinct cards look equal.
  const isOpen =
    !!showSplitter &&
    showSplitter._previewKey != null &&
    showSplitter._previewKey === content._previewKey
  // ENG-1781: api-getorbitx stamps a PptBuilderV2 STUB ui_component
  // (params = {source, topic_label} only) on EVERY proactive_proposal
  // turn so the session shows in the dropdown the moment the POST returns.
  // The real deck turn overwrites that stub with full params
  // (ppt_session_id / text_body / mode=iterate / …). But a NON-deck
  // follow-up in the same session (e.g. "find case studies …") keeps the
  // bare stub, which rendered as a duplicate "PowerPoint — <topic>"
  // Preview card that opens nothing on click (the reported dead click).
  // Like a normal Ask-OrbitShift card, only render when there's a real
  // deck to open — suppress the marker-only stub.
  const isPptStubOnly =
    resourceName === "PptBuilderV2" &&
    !content?.resourceParams?.ppt_session_id &&
    !content?.resourceParams?.text_body &&
    content?.resourceParams?.mode !== "iterate"
  if (isPptStubOnly) return null

  return (
    <>
      <button
        aria-pressed={isOpen}
        className={`rounded-lg min-w-52 max-w-60 w-fit overflow-hidden ml-11 border transition-shadow ${
          isOpen
            ? "border-primary ring-2 ring-primary/50 dark:ring-primary-light/50 shadow-sm"
            : "border-primary dark:border-gray-400"
        }`}
        onClick={() => {
          if (!isOpen && isPptAgent && pptSessionId && tokenInfo) {
            import("~/api/ppt-ai-generator").then(({ fetchPptSession }) => {
              fetchPptSession(tokenInfo, pptSessionId)
                .then((res: any) => {
                  const session = res?.data?.data || res?.data
                  if (!session) return
                  const setPptState =
                    useOsAgentsIdStore.getState().setPptOutlineState
                  const requestedCount = session.messages?.[0]?.slide_count || 8
                  if (session.slides && session.slides.length > 0) {
                    const outlineCount =
                      session.outline?.length || requestedCount
                    const slidesComplete = session.slides.length >= outlineCount
                    const isIterating = session.status === "iterating"
                    setPptState({
                      slidesStatus: isIterating
                        ? "generating"
                        : slidesComplete
                          ? "ready"
                          : "generating",
                      cards: mapOutline(session.outline || []),
                      slides: session.slides,
                      requestedCount,
                      isStreaming: false
                    })
                  } else if (session.outline) {
                    const isIncomplete = session.outline.length < requestedCount
                    setPptState({
                      cards: mapOutline(session.outline),
                      requestedCount,
                      isStreaming: isIncomplete
                    })
                  }
                })
                .catch((_) => {
                  void _
                })
            })
          }
          setShowSplitter(
            isOpen ? null : { ...content, _previewCardTitle: backendTitle }
          )
          track(EVENTS.MESSAGE_CARD_CLICKED, {
            ...getBaseEventProps({ feature: "os_agents" }),
            tool_id: resourceName
          })
        }}
      >
        <div className="px-3 py-2 bg-genesis dark:bg-dark-2 flex items-center gap-3 text-left text-dark-2 dark:text-dark-6">
          <FileText size={20} className="flex-shrink-0" />
          <p className="text-xs">{label}</p>
        </div>
        <div className="px-3 py-2 flex justify-between items-center gap-3 text-dark-2 dark:text-dark-6">
          <p className="text-xs">{t("label_preview")}</p>
          <ChevronRight size={16} />
        </div>
      </button>
      {/* Generate Slides button hidden — not functional yet, will be re-introduced later */}
    </>
  )
}
